import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { readServerChartPreference, writeServerChartPreference } from '../../../lib/chart/chartPreferenceStore.js'

// Server is the only durable source. The in-memory entry keeps pending edits
// across pane switches and serializes writes/read-after-write for one identity.
const entries = new Map()
function entryFor(userId, preferenceKey, normalize, view) {
  const key = JSON.stringify([userId, preferenceKey, view])
  if (entries.has(key)) return entries.get(key)
  const args = { userId, scope: view === 'shared' ? 'chart-drawings-v2' : `chart-drawings-${view}-v3`, preferenceKey }
  const entry = { snapshot: { items: [], status: 'loading' }, listeners: new Set(), queue: Promise.resolve(), revision: 0, started: false }
  const publish = patch => {
    entry.snapshot = { ...entry.snapshot, ...patch }
    entry.listeners.forEach(fn => fn())
  }
  entry.load = async () => {
    if (entry.snapshot.status === 'saving' || entry.snapshot.status === 'save-error') return
    publish({ status: 'loading' })
    try {
      if (!userId) throw new Error('Sign in required')
      await entry.queue
      let value = await readServerChartPreference({ ...args, throwOnError: true })
      // A saved (including empty) isolated record is authoritative. Read-only
      // import preserves old scopes for rollback and never resurrects deleted IDs.
      if (!value && view !== 'shared') {
        const shared = await readServerChartPreference({ ...args, scope: 'chart-drawings-v2', throwOnError: true })
        const legacy = await readServerChartPreference({ ...args, scope: 'relative-strength-drawings', throwOnError: true })
        for (const record of [shared, legacy]) {
          if (record && ![1, 2].includes(record.version)) throw new Error('Unsupported drawing version')
        }
        const rsIds = new Set((legacy?.items || []).map(item => item.id))
        const source = shared?.items || legacy?.items || []
        value = { version: 2, items: source.filter(item => view === 'rs' ? rsIds.has(item.id) : !rsIds.has(item.id))
          .map(item => view === 'rs' && !item.timeframes?.length ? { ...item, timeframes: ['daily'] } : item) }
      }
      if (!value) value = await readServerChartPreference({ ...args, scope: 'relative-strength-drawings', throwOnError: true })
      if (value && ![1, 2].includes(value.version)) throw new Error('Unsupported drawing version')
      publish({ items: normalize(value?.items || []), status: 'ready' })
    } catch { publish({ status: 'load-error' }) }
  }
  entry.save = items => {
    if (!userId || ['loading', 'load-error'].includes(entry.snapshot.status)) return
    const next = normalize(items), revision = ++entry.revision
    publish({ items: next, status: 'saving' })
    entry.queue = entry.queue.catch(() => {}).then(async () => {
      if (revision !== entry.revision) return
      const ok = await writeServerChartPreference({ ...args, preferences: { version: 2, items: next } })
      if (revision === entry.revision) publish({ status: ok ? 'ready' : 'save-error' })
    }).catch(() => { if (revision === entry.revision) publish({ status: 'save-error' }) })
  }
  entry.retry = () => entry.snapshot.status === 'save-error' ? entry.save(entry.snapshot.items) : entry.load()
  entries.set(key, entry)
  return entry
}
export function useServerDrawingRecord(userId, preferenceKey, normalize, view = 'shared') {
  const entry = useMemo(() => entryFor(userId, preferenceKey, normalize, view), [userId, preferenceKey, normalize, view])
  const snapshot = useSyncExternalStore(callback => { entry.listeners.add(callback); return () => entry.listeners.delete(callback) }, () => entry.snapshot)
  useEffect(() => { if (!entry.started) { entry.started = true; entry.load() } }, [entry])
  useEffect(() => {
    if (!['saving', 'save-error'].includes(snapshot.status)) return undefined
    const warn = event => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [snapshot.status])
  return { ...snapshot, setItems: entry.save, retry: entry.retry }
}
